import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import FormBox from "../../components/FormBox/FormBox";
import {
  fetchPayoffDemandsDetail,
  updatePayoffDemandsDetail,
} from "../../store/reducers/dashboardSlice/api";
import { useAbortController } from "../../hooks/useAbortController";
import { LoadingButton, SpinnerBlock } from "../../Utils/CustomComponent";
import { toast } from "react-toastify";
import { commonToast } from "../../Utils";
import PopupWrapper from "../../components/PopupWrapper";
import axiosInstance from "../../axios";
import urls from "../../Utils/urls";

const preUrl = isUATEnvironment()
  ? "https://objectstorage.us-sanjose-1.oraclecloud.com/n/axlalvgc2fil/b/Notification_Outbound_UAT/o/"
  : "https://objectstorage.us-sanjose-1.oraclecloud.com/n/axlalvgc2fil/b/Notification_Outbound/o/";
const updatedPreUrl = isUATEnvironment()
  ? "https://objectstorage.us-sanjose-1.oraclecloud.com/p/TCNHagN5F8QOIvJkIFDAy0v3zFltYoNv36YacPtbZo3ZCUOdcrkSURLvhYKS6lez/n/axlalvgc2fil/b/Notification_Outbound_UAT/o/"
  : "https://objectstorage.us-sanjose-1.oraclecloud.com/p/j6Zlid5QFgH9pdGSk0-kFt1Bxkcu--ALbrIhg3Td-LSks9IubJgl_OqjMJVd0NWb/n/axlalvgc2fil/b/Notification_Outbound/o/";

const PayOffDetailScreen = () => {
  const navigate = useNavigate();
  const { payoffId, status: payoffStatus } = useParams();
  const dispatch = useDispatch();
  const { signal } = useAbortController();

  const data = useSelector((state) => state.dashboard.payOffDemandsDetail.data);
  const user = useSelector((state) => state.RS_Authentication.user);
  const status = useSelector(
    (state) => state.dashboard.payOffDemandsDetail.status,
  );
  const accountId = useSelector((state) => state.account.lenderId);
  const selectedRole = useSelector(
    (state) => state?.account?.selectedRole?.value,
  );
  const [activeTab, setActiveTab] = useState("loanDetails");
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineComment, setDeclineComment] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [declineLoader, setDeclineLoader] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const userDetails = JSON.parse(localStorage.getItem("accessed_user"));
  let timeoutId;

  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  useEffect(() => {
    return () => {
      clearTimeout(timeoutId); // prevent memory leaks on unmount
    };
  }, []);

  useEffect(() => {
    dispatch(
      fetchPayoffDemandsDetail({
        payoffId,
        email: user?.details?.email,
        accountId,
        signal,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    if (data) prefillFormData(data, setValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "loanDetails":
        return (
          <RenderComp
            register={register}
            watch={watch}
            setValue={setValue}
            options={loanDetailsConfig}
            errors={errors}
          />
        );
      case "charges":
        return <ChargesTable charges={data?.charges || []} />;
      case "fees":
        return <FeesTable fees={data?.fees || []} />;
      case "recipients":
        return <RecipientsTable recipients={data?.recipients || []} />;
      case "communications":
        return (
          <CommunicationsTable communications={data?.communications || []} />
        );
      case "attachments":
        return (
          <AttachmentsTable
            attachments={data?.attachments || []}
            demandAttachment={data?.demand_details?.attachment}
          />
        );
      default:
        return <div>Select a tab to view content</div>;
    }
  };

  if (status === "loading")
    return (
      <div className="d-flex align-items-center">
        <SpinnerBlock height="50vh" />
      </div>
    );

  const approver = data?.approver?.find((item) => {
    const emails = item?.recipient_email
      ?.split(",")
      ?.map((e) => e.trim().toLowerCase());

    return emails?.includes(userDetails?.email?.toLowerCase());
  });

  const handleButtonClick = (buttonId) => {
    if (buttonId === "approve") {
      setApproveLoading(true);
      const payload = {
        payoff_id: Number(payoffId),
        email_id: approver?.email_id,
        ownerredid: approver?.ownerredid,
        status_flg: "A",
        comment: null,
        created_by: user?.details?.email,
      };

      updatePayoffDemandsDetail(payload, signal);
      // Set 1-minute timeout before navigation
      timeoutId = setTimeout(() => {
        navigate("/demand-payoff");
        setApproveLoading(false);
      }, 5000); // 60 seconds
    } else if (buttonId === "decline") {
      setShowDeclineModal(true);
    }
  };

  const handleDeclineSubmit = () => {
    setDeclineLoader(true);
    const payload = {
      payoff_id: Number(payoffId),
      email_id: approver?.email_id,
      ownerredid: approver?.ownerredid,
      status_flg: "R",
      comment: declineComment,
      created_by: user?.details?.email,
    };

    updatePayoffDemandsDetail(payload, signal);
    // Set 1-minute timeout before navigation
    timeoutId = setTimeout(() => {
      navigate("/demand-payoff");
      setDeclineLoader(false);
    }, 10000); // 60 seconds
    // navigate("/demand-payoff");
    setShowDeclineModal(false);
    setDeclineComment("");
  };

  const handleDeclineCancel = () => {
    setShowDeclineModal(false);
    setDeclineComment("");
  };

  return (
    <div
      className="text-start container-fluid my-0 px-0"
      style={{ overflowY: "scroll" }}
    >
      <section
        className="rounded"
        style={{ background: "#eeeeee", minHeight: "100vh" }}
      >
        <article className="container-fluid">
          <div className="p-3 pb-0 mt-1 d-flex justify-content-between align-items-center">
            <h6>
              <a href="/demand-payoff" style={{ textDecoration: "none" }}>
                Payoff Request
              </a>{" "}
              /{" "}
              {payoffStatus === "Pending"
                ? "Requested Payoff"
                : payoffStatus === "Pending Lender Approval"
                  ? "My Pending Approval"
                  : payoffStatus === "Approved"
                    ? "Approved Payoff"
                    : payoffStatus === "Rejected" && "Rejected Payoff"}
            </h6>
            {payoffStatus !== "Approved" &&
            payoffStatus !== "Rejected" &&
            payoffStatus !== "Pending" &&
            (approver?.recipient_name === "Main Approval" ||
              approver?.recipient_name === "Additional Approver") &&
            approver?.send_for_approval === "Y" &&
            !approver.status_flg &&
            selectedRole === "L" ? (
              <div className="d-flex gap-2 m-4">
                <LoadingButton
                  type="button"
                  className="btn btn-success mt-3 text-white"
                  onClick={() => handleButtonClick("approve")}
                  title={"Approve"}
                  disabled={approveLoading || declineLoader}
                  loading={approveLoading}
                />

                <LoadingButton
                  type="button"
                  className="btn btn-error mt-3 text-white"
                  onClick={() => handleButtonClick("decline")}
                  title="Decline"
                  disabled={declineLoader || approveLoading}
                  loading={declineLoader}
                />
              </div>
            ) : payoffStatus !== "Approved" &&
              payoffStatus !== "Rejected" &&
              payoffStatus !== "Pending" &&
              (approver?.recipient_name === "Main Approval" ||
                approver?.recipient_name === "Additional Approver") &&
              approver?.send_for_approval === "N" &&
              selectedRole === "L" ? (
              <></>
            ) : (
              payoffStatus !== "Approved" &&
              payoffStatus !== "Rejected" &&
              payoffStatus !== "Pending" &&
              !approver?.status_flg &&
              selectedRole === "L" && (
                <div className="d-flex gap-2 m-4">
                  <LoadingButton
                    type="button"
                    className="btn btn-success mt-3 text-white"
                    onClick={() => handleButtonClick("approve")}
                    title={"Approve"}
                    disabled={approveLoading || declineLoader}
                    loading={approveLoading}
                  />

                  <LoadingButton
                    type="button"
                    className="btn btn-error mt-3 text-white"
                    onClick={() => handleButtonClick("decline")}
                    title="Decline"
                    disabled={declineLoader || approveLoading}
                    loading={declineLoader}
                  />
                </div>
              )
            )}
          </div>
        </article>

        <article className="container-fluid">
          <section className="bg-white p-5 my-2 mx-4">
            <div className="row">
              <div className="col-md-5 col-sm-12">
                <RenderComp
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  options={leftSideFormConfig(setShowAlert)}
                  errors={errors}
                />
              </div>
              <div className="col-md-7 col-sm-12">
                <ul className="nav nav-tabs mb-4">
                  {tabs.map((tab) => (
                    <li className="mb-4" key={tab.id}>
                      <button
                        className={`border ${
                          activeTab === tab.id
                            ? "text-primary border-primary"
                            : ""
                        }`}
                        style={{
                          background: "#fff",
                          fontSize: "16px",
                          padding: "5px 8px",
                        }}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="tab-content">{renderTabContent()}</div>
              </div>
            </div>
          </section>
        </article>
      </section>

      {/* Decline Modal */}
      {showDeclineModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">On Hold Demands</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleDeclineCancel}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p className="text-danger mb-3">
                    This will send demand request form to SLS.
                  </p>
                  <div className="mb-3">
                    <label className="form-label">
                      <strong>Loan Number</strong>
                    </label>
                    <div className="form-control bg-light">
                      {data?.demand_details?.loan_number || "-"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="declineComment" className="form-label">
                      <strong>Comment</strong>
                    </label>
                    <textarea
                      id="declineComment"
                      className="form-control"
                      rows="4"
                      value={declineComment}
                      onChange={(e) => setDeclineComment(e.target.value)}
                      placeholder="Enter reason for decline..."
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDeclineCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDeclineSubmit}
                  style={{ backgroundColor: "#17a2b8", borderColor: "#17a2b8" }}
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <PayOffLetterModal
        showAlert={showAlert}
        setShowAlert={setShowAlert}
        payoffId={payoffId}
      />
    </div>
  );
};

const RenderComp = ({ options, ...props }) => (
  <div className="border p-3">
    {options.map((data, index) => (
      <div key={index} className={`${data.className}`}>
        <h4 className="mt-3 mb-4">{data.section}</h4>
        <FormBox {...props} options={data.arr} />
      </div>
    ))}
  </div>
);

export default PayOffDetailScreen;

const PayOffLetterModal = ({ showAlert, setShowAlert, payoffId }) => {
  const [iframeLoad, setIframeLoad] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    async function getPreviewLetter(payoffId) {
      try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Basic UkVTVEFQSUxPQU5MSU5ROjg4V2VIZWxwVSQjQA==",
          },
        };

        const res = await axiosInstance.post(
          urls.payOffDemand.previewLetter(payoffId),
          {},
          config,
        );
        const fileUrl = res?.data?.PDF_URL;
        const updatedFileUrl = fileUrl?.replace(preUrl, updatedPreUrl);
        setPdfUrl(updatedFileUrl);
      } catch (error) {
        console.error(error);
      }
    }

    if (payoffId && showAlert) {
      getPreviewLetter(payoffId);
    }
  }, [payoffId, showAlert]);

  return (
    <PopupWrapper
      title="Preview Payoff Letter"
      extraClass="width-800"
      Component={
        <div>
          {iframeLoad && (
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", top: "18rem", left: "45%" }}>
                <SpinnerBlock height="50vh" />
              </div>
            </div>
          )}
          <iframe
            src={pdfUrl}
            width="100%"
            style={{
              height: "85vh", // or any value like '100vh', '70vh', etc.
              border: "none",
            }}
            title="PDF Preview"
            onLoad={() => setIframeLoad(false)}
          />
        </div>
      }
      toShow={showAlert}
      closeEventHandler={() => {
        setShowAlert(false);
        setIframeLoad(true);
      }}
      bodyClass={"p-0"}
      titleClassName="px-5"
    />
  );
};

const leftSideFormConfig = (setShowAlert) => [
  {
    section: "",
    className: "border p-3",
    arr: [
      {
        title: "Payoff Good Through Date",
        field: "payoffGoodThroughDate",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      {
        title: "Demand Expiration Date",
        field: "demandExpirationDate",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      {
        title: "Payoff Requested Date",
        field: "payoffRequestedDate",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
    ],
  },
  {
    section: "",
    arr: [
      {
        title: "Default Interest Start Date",
        field: "defaultInterestStartDate",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
        defaultValue: "-",
      },
      // {
      //   title: "Principal Balance",
      //   field: "principalBalanceDemand",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      // {
      //   title: "Principal Balance",
      //   field: "myPrincipalBalance",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      {
        title: "Default Interest End Date",
        field: "defaultInterestEndDate",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
        defaultValue: "-",
      },
      // {
      //   title: "Accrued Interest",
      //   field: "accruedInterest",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      // {
      //   title: "Accrued Interest",
      //   field: "myAccruedInterest",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      {
        title: "Default Interest Rate",
        field: "defaultInterestRate",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
        defaultValue: "0%",
      },
      // {
      //   title: "Trust Balance",
      //   field: "trustBalance",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      // {
      //   title: "Trust Balance",
      //   field: "myTrustBalance",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      {
        title: "Default Interest",
        field: "defaultInterest",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
        defaultValue: "$0.00",
      },
      // {
      //   title: "Late Charges",
      //   field: "lateCharges",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      // {
      //   title: "Late Charges",
      //   field: "myLateCharges",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      {
        title: "Principal Balance",
        field: "principalBalanceDemand",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },

      // {
      //   title: "Unpaid Charges",
      //   field: "myUnpaidCharges",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      {
        title: "Accrued Interest",
        field: "accruedInterest",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      // {
      //   title: "Prepayment Penalties",
      //   field: "myPrepaymentPenalties",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },

      {
        title: "Unpaid Interest",
        field: "unpaidInterest",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      // {
      //   title: "Other Payments",
      //   field: "myOtherPayments",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      {
        title: "Trust Balance",
        field: "trustBalance",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      // {
      //   title: "Payoff Amount",
      //   field: "myPayoffAmount",
      //   type: "text",
      //   className: "col-6 mb-3",
      //   disable: true,
      // },
      {
        title: "Late Charges",
        field: "lateCharges",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      {
        type: "content",
        content: "",
        className: "col-6",
      },
      {
        title: "Unpaid Charges",
        field: "unpaidCharges",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      {
        type: "content",
        content: "",
        className: "col-6",
      },

      {
        title: "Prepayment Penalties",
        field: "prepaymentPenalties",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      {
        type: "content",
        content: "",
        className: "col-6",
      },

      {
        title: "Other Payments",
        field: "otherPayments",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      {
        type: "content",
        content: "",
        className: "col-6",
      },
      {
        title: "Payoff Amount",
        field: "payoffAmount",
        type: "text",
        className: "col-6 mb-3",
        disable: true,
      },
      {
        type: "content",
        content: "",
        className: "col-6",
      },
      {
        type: "content",
        content: "",
        className: "col-6",
      },
      {
        type: "content",
        content: (
          <div className="text-end">
            <button
              className="btn btn-primary"
              onClick={() => setShowAlert(true)}
            >
              Preview Payoff Letter
            </button>
          </div>
        ),
        className: "col-6",
      },
    ],
  },
];

const loanDetailsConfig = [
  {
    section: "",
    arr: [
      {
        title: "Account",
        field: "account",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Name",
        field: "name",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Contact Person Name",
        field: "contactPersonName",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Maturity Date",
        field: "maturityDate",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Paid off Date",
        field: "paidOffDate",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Next Payment",
        field: "nextPayment",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Billed Through",
        field: "billedThrough",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Note Rate",
        field: "noteRate",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Principal Balance",
        field: "principalBalanceLoan",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Daily Rate Using",
        field: "dailyRate",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "30 Days / Actual",
        field: "thirtyDaysActual",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Phone",
        field: "phone",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Email",
        field: "email",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "Address",
        field: "address",
        type: "text",
        className: "col-12",
        disable: true,
      },
      {
        title: "County",
        field: "county",
        type: "text",
        className: "col-12",
        disable: true,
      },
    ],
  },
];

const tabs = [
  { id: "loanDetails", label: "Loan Details", active: true },
  { id: "charges", label: "Charges" },
  { id: "fees", label: "Fees" },
  { id: "recipients", label: "Recipients" },
  { id: "communications", label: "Communications" },
  { id: "attachments", label: "Attachments" },
];

const prefillFormData = (data, setValue) => {
  const d = data.demand_details || {};
  const l = data.loan_details || {};
  const my = data.myContribution || {};

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    // minimumFractionDigits: 2,
    // maximumFractionDigits: 2,
  });

  setValue("payoffGoodThroughDate", d.payoff_good_through_date || "");
  setValue("demandExpirationDate", d.demand_expiration_date || "");
  setValue("payoffRequestedDate", d.demand_request_date || "");

  setValue(
    "principalBalanceDemand",
    currencyFormatter.format(l.prinbal || 0),
    // `$${Number(l.prinbal || 0).toLocaleString()}`
  );
  setValue(
    "accruedInterest",
    currencyFormatter.format(d.calculated_payoff || 0),
    // `$${Number(d.calculated_payoff || 0).toLocaleString()}`
  );
  setValue("unpaidInterest", currencyFormatter.format(l.unpaidinterest || 0));
  setValue(
    "trustBalance",
    currencyFormatter.format(d.trust_balance || 0),
    // `$${Number(d.trust_balance || 0).toLocaleString()}`
  );
  setValue(
    "lateCharges",
    currencyFormatter.format(d.late_charges || 0),
    // `$${Number(d.late_charges || 0).toFixed(2)}`
  );
  setValue(
    "unpaidCharges",
    currencyFormatter.format(d.unpaid_charges || 0),
    // `$${Number(d.unpaid_charges || 0).toFixed(2)}`
  );
  setValue(
    "prepaymentPenalties",
    currencyFormatter.format(d.prepayment_penalties || 0),
    // `$${Number(d.prepayment_penalties || 0).toFixed(2)}`
  );
  setValue(
    "otherPayments",
    currencyFormatter.format(d.other_payments || 0),
    // `$${Number(d.other_payments || 0).toFixed(2)}`
  );

  // const total = [
  //   "prinbal",
  //   "calculated_payoff",
  //   "trust_balance",
  //   "late_charges",
  //   "unpaid_charges",
  //   "prepayment_penalties",
  //   "other_payments",
  // ]
  //   .map((key) => Number(d[key] || l[key] || 0))
  //   .reduce((a, b) => a + b, 0);
  setValue(
    "payoffAmount",
    currencyFormatter.format(d.payoff_amount || 0),
    // `$${Number(d.payoff_amount || 0).toLocaleString()}`
  );

  setValue(
    "myPrincipalBalance",
    currencyFormatter.format(my.principal_balance || 0),
    // `$${Number(my.principal_balance || 0).toLocaleString()}`
  );
  setValue(
    "myAccruedInterest",
    currencyFormatter.format(my.accured_intrest || 0),
    // `$${Number(my.accured_intrest || 0).toLocaleString()}`
  );
  setValue(
    "myTrustBalance",
    currencyFormatter.format(my.trust_balance || 0),
    // `$${Number(my.trust_balance || 0).toLocaleString()}`
  );
  setValue(
    "myLateCharges",
    currencyFormatter.format(my.late_charges || 0),
    // `$${Number(my.late_charges || 0).toLocaleString()}`
  );
  setValue(
    "myUnpaidCharges",
    currencyFormatter.format(my.unpaid_charges || 0),
    // `$${Number(my.unpaid_charges || 0).toLocaleString()}`
  );
  setValue(
    "myPrepaymentPenalties",
    currencyFormatter.format(my.prepayment_penalties || 0),
    // `$${Number(my.prepayment_penalties || 0).toLocaleString()}`
  );
  setValue(
    "myOtherPayments",
    currencyFormatter.format(my.other_payments || 0),
    // `$${Number(my.other_payments || 0).toLocaleString()}`
  );
  setValue(
    "myPayoffAmount",
    currencyFormatter.format(my.payoff_amount || 0),
    // `$${Number(my.payoff_amount || 0).toLocaleString()}`
  );

  setValue("account", d.loan_number || "");
  setValue("name", l.name || "");
  setValue("phone", l.phonecell || "-");
  setValue("email", l.emailaddress || "");
  setValue("address", l.address || "");
  setValue("county", l.county || "");
  setValue("dailyRate", l.daily_rate_using || "");
  setValue("contactPersonName", l.contact_person_name || "");
  setValue("maturityDate", l.maturitydate || "");
  setValue("paidOffDate", l.paidoffdate || "-");
  setValue("nextPayment", l.nextduedate || "");
  setValue("billedThrough", l.Billed_Through || "");
  setValue("noteRate", `${l.noterate || ""}%`);
  setValue(
    "principalBalanceLoan",
    currencyFormatter.format(l.prinbal || 0),
    // `$${Number(l.prinbal || 0).toLocaleString()}`
  );
  setValue(
    "thirtyDaysActual",
    l["30_Days_Actual"] == "1" ? "30 Days" : "Actual" || "",
  );
};

const ChargesTable = ({ charges = [] }) => {
  const safeCharges = charges || [];
  const total = safeCharges.reduce(
    (acc, charge) => acc + Number(charge?.amount || 0),
    0,
  );

  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr className="text-center">
            <th>Charge Date</th>
            {/* <th>Charge Type</th> */}
            <th>Description</th>
            <th>Amount</th>
            <th>Charge To</th>
            <th>Deferred</th>
            <th>Assess Finance Charges</th>
            <th>Owed to</th>
            <th>Owed by</th>
            <th>Int Rate</th>
          </tr>
        </thead>
        <tbody>
          {safeCharges.length > 0 ? (
            safeCharges.map((charge, index) => (
              <tr key={index}>
                <td>{charge?.charge_date || "-"}</td>
                {/* <td>{charge?.charge_type || "-"}</td> */}
                <td>{charge?.description || "-"}</td>
                <td>${charge?.amount || "0.00"}</td>
                <td>{charge?.charge_to || "-"}</td>
                <td>
                  {charge?.deferred == "1" ? (
                    <div className="form-group d-flex align-items-center justify-content-center">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="deferred"
                          id="deferred"
                          disabled
                          checked={true}
                        />
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </td>
                <td>
                  {charge?.assess_finance_charges == "1" ? (
                    <div className="form-group d-flex align-items-center justify-content-center">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="assess-finance"
                          id="assess-finance"
                          disabled
                          checked={true}
                        />
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </td>
                <td>{charge?.owed_to}</td>
                <td>{charge?.owed_by}</td>
                <td>
                  {`${charge.int_rate !== null ? charge.int_rate : "-"}` || "-"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="text-center">
                No charges available
              </td>
            </tr>
          )}
          {safeCharges.length > 0 && (
            <tr>
              <td>
                <strong>Total</strong>
              </td>
              <td></td>
              <td>
                <strong>${total.toFixed(2)}</strong>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const FeesTable = ({ fees = [] }) => {
  const safeFees = fees || [];
  const total = safeFees.reduce(
    (acc, fee) => acc + Number(fee?.fee_amount || 0),
    0,
  );

  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Fee Description</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {safeFees.length > 0 ? (
            safeFees.map((fee, index) => (
              <tr key={index}>
                <td>{fee?.fee_description || "-"}</td>
                <td>${fee?.fee_amount || "0.00"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="2" className="text-center">
                No fees available
              </td>
            </tr>
          )}
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td>
              <strong>${total.toFixed(2)}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const RecipientsTable = ({ recipients = [] }) => {
  const safeRecipients = recipients || [];

  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Recipient Name</th>
            <th>Recipient Email</th>
            <th>Primary Recipient?</th>
            {/* <th>Status</th> */}
          </tr>
        </thead>
        <tbody>
          {safeRecipients.length > 0 ? (
            safeRecipients.map((recipient, index) => (
              <tr key={`r-${index}`}>
                <td>{recipient?.recipient_name || "-"}</td>
                <td>{recipient?.recipient_email || "-"}</td>
                <td>
                  <div className="form-group d-flex align-items-center justify-content-center">
                    <div className="form-check">
                      <input
                        className="form-check-input border-2"
                        type="radio"
                        name={`deferred_${index}`}
                        id={`deferred_${index}`}
                        disabled
                        checked={
                          recipient?.primary_recipient_yn === "Y" ? true : false
                        }
                      />
                    </div>
                  </div>
                </td>
                {/* <td>{recipient?.status_flg || "Pending"}</td> */}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="text-center">
                No recipients available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const CommunicationsTable = ({ communications = [] }) => {
  const safeCommunications = communications || [];

  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Note</th>
            <th>Created By</th>
            <th>Creation Date</th>
          </tr>
        </thead>
        <tbody>
          {safeCommunications.length > 0 ? (
            safeCommunications.map((comm, index) => (
              <tr key={index}>
                <td>{comm?.["note "] || comm?.note || "-"}</td>
                <td>{comm?.created_by || "-"}</td>
                <td>
                  {comm?.creation_date
                    ? new Date(comm.creation_date).toLocaleDateString()
                    : "-"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="text-center">
                No communications available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const AttachmentsTable = ({ attachments = [], demandAttachment }) => {
  const safeAttachments = attachments || [];
  const hasAttachments = safeAttachments.length > 0;

  async function downloadAttachment(fileUrl, name) {
    const id = toast.loading("Downloading in progress...");
    try {
      const updatedFileUrl = fileUrl?.replace(preUrl, updatedPreUrl);

      const res = await fetch(updatedFileUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name; // exact name you want
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.update(id, {
        render: "Document Downloaded Successfully",
        type: "success",
        isLoading: false,
        ...commonToast,
      });
    } catch (error) {
      console.error("Error fetching the document:", error);
      toast.update(id, {
        render: "SOME ERROR OCCURED",
        type: "error",
        isLoading: false,
        ...commonToast,
      });
    }
  }

  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>File Name</th>
            {/* <th>Created By</th>
            <th>Creation Date</th> */}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {safeAttachments.length > 0 &&
            safeAttachments.map((attachment, index) => (
              <tr key={index}>
                <td style={{ wordBreak: "break-all" }}>
                  {attachment?.file_name || "-"}
                </td>
                {/* <td>{attachment?.created_by || "-"}</td>
                <td>
                  {attachment?.creation_date
                    ? new Date(attachment.creation_date).toLocaleDateString()
                    : "-"}
                </td> */}
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      downloadAttachment(attachment.url, attachment.file_name)
                    }
                  >
                    Download
                  </button>
                </td>
              </tr>
            ))}
          {/* {demandAttachment && (
            <tr>
              <td>Payoff Demand PDF</td>
              <td>System</td>
              <td>-</td>
              <td>
                <a
                  href={demandAttachment}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  View
                </a>
              </td>
            </tr>
          )} */}
          {!hasAttachments && (
            <tr>
              <td colSpan="4" className="text-center">
                No attachments available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

function isUATEnvironment() {
  // Check if the current URL contains 'uat'
  return window.location.href.toLowerCase().includes("uat");
}
